import { LoadingState } from "@/components/ui/loading-state";
import { useEffect, useReducer, useRef, useState } from "react";

import { BackendError } from "@/lib/backend";
import type {
  BuilderLegalStructureResponse,
  BuilderLegalStructureSourceDocument,
  BuilderLegalEntityAttachmentMutation,
  BuilderLegalEntityAttachmentStatus,
  SaveBuilderLegalStructureRequest,
} from "@/lib/api/databook";

import { LegalStructureEditor } from "./LegalStructureEditor";
import type { LegalEntityDraftAttachment } from "./LegalEntityCard";
import {
  buildLegalStructureSavePayload,
  initialLegalStructureAdapterState,
  isLegalStructureUndoShortcut,
  legalStructureAdapterReducer,
  legalStructureSaveErrorMessage,
} from "./legalStructureAdapter";
import {
  legalStructuresHaveSameWritableState,
  type LegalStructureAnalysisInterval,
} from "./legalStructure";

export type LegalEntitiesLoadState =
  | { status: "loading" }
  | { status: "loaded"; resource: BuilderLegalStructureResponse }
  | { status: "failed" };

interface LegalEntitiesStageProps {
  state: LegalEntitiesLoadState;
  onRetry: () => void;
  onSave: (
    payload: SaveBuilderLegalStructureRequest,
  ) => Promise<BuilderLegalStructureResponse>;
  onSaveWithAttachments?: (
    payload: SaveBuilderLegalStructureRequest,
  ) => Promise<BuilderLegalEntityAttachmentMutation>;
  onGetAttachmentStatus?: (
    jobId: string,
  ) => Promise<BuilderLegalEntityAttachmentStatus>;
  onUploadAttachments?: (
    files: File[],
  ) => Promise<Array<{ documentId: string; filename: string }>>;
  onDownloadAttachment?: (documentId: string, filename: string) => void;
  onContinue: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onPendingChange?: (pending: boolean) => void;
  analysisInterval?: LegalStructureAnalysisInterval;
  onDownloadSource?: (source: BuilderLegalStructureSourceDocument) => void;
  attachmentPollIntervalMs?: number;
}

export function LegalEntitiesStage({
  state,
  onRetry,
  onSave,
  onSaveWithAttachments,
  onGetAttachmentStatus,
  onUploadAttachments,
  onDownloadAttachment,
  onContinue,
  onDirtyChange,
  onPendingChange,
  analysisInterval,
  onDownloadSource,
  attachmentPollIntervalMs = 1500,
}: LegalEntitiesStageProps) {
  const [editorState, dispatch] = useReducer(
    legalStructureAdapterReducer,
    initialLegalStructureAdapterState,
  );
  const [draftAttachments, setDraftAttachments] = useState<
    Record<string, LegalEntityDraftAttachment[]>
  >({});
  const attachmentSequence = useRef(0);
  const persistedStructure = state.status === "loaded"
    ? state.resource.structure
    : null;
  const currentEntityIds = new Set(
    (editorState.draft ?? persistedStructure)?.entities.map((entity) => entity.id),
  );
  const relevantAttachments = Object.fromEntries(
    Object.entries(draftAttachments).filter(([entityId]) =>
      currentEntityIds.has(entityId)),
  );
  const changesPending = editorState.saving
    || Object.values(relevantAttachments).some((attachments) => attachments.length > 0);
  useEffect(() => {
    onPendingChange?.(changesPending);
  }, [changesPending, onPendingChange]);
  const undoContext = useRef({
    editorState,
    persistedStructure,
    onDirtyChange,
  });
  undoContext.current = {
    editorState,
    persistedStructure,
    onDirtyChange,
  };

  useEffect(() => {
    const undo = (event: KeyboardEvent) => {
      if (!isLegalStructureUndoShortcut(event)) return;
      const context = undoContext.current;
      const previousStructure = context.editorState.undoStack.at(-1);
      if (
        context.editorState.saving
        || !context.persistedStructure
        || !previousStructure
      ) {
        return;
      }
      event.preventDefault();
      dispatch({
        type: "undo",
        persistedStructure: context.persistedStructure,
      });
      context.onDirtyChange?.(
        !legalStructuresHaveSameWritableState(
          previousStructure,
          context.persistedStructure,
        ),
      );
    };

    window.addEventListener("keydown", undo);
    return () => window.removeEventListener("keydown", undo);
  }, []);

  if (state.status === "loading") {
    return <LoadingState />;
  }
  if (state.status === "failed") {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="rounded-xl border border-destructive/30 bg-card p-6 text-center">
          <p className="font-semibold">Legal structure could not be loaded</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 rounded-lg bg-foreground px-4 py-2 text-[12.5px] font-medium text-background"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const resource = state.resource;
  const value = editorState.draft ?? resource.structure;
  const attachmentsBusy = Object.values(relevantAttachments).some(
    (attachments) => attachments.some((attachment) => attachment.status === "uploading"),
  );
  const attachmentsInvalid = Object.values(relevantAttachments).some(
    (attachments) => attachments.some((attachment) => attachment.status === "error"),
  );
  const attachmentIdsByEntity = Object.fromEntries(
    Object.entries(relevantAttachments).map(([entityId, attachments]) => [
      entityId,
      attachments.flatMap((attachment) =>
        attachment.status === "ready" && attachment.documentId
          ? [attachment.documentId]
          : []),
    ]),
  );
  const hasReadyAttachments = Object.values(attachmentIdsByEntity).some(
    (documentIds) => documentIds.length > 0,
  );

  const attachFiles = async (entityId: string, files: File[]) => {
    if (!onUploadAttachments || files.length === 0) return;
    const optimistic = files.map((file) => ({
      key: `legal-entity-upload-${attachmentSequence.current++}`,
      documentId: null,
      filename: file.name,
      status: "uploading" as const,
    }));
    setDraftAttachments((current) => ({
      ...current,
      [entityId]: [...(current[entityId] ?? []), ...optimistic],
    }));
    try {
      const uploaded = await onUploadAttachments(files);
      setDraftAttachments((current) => ({
        ...current,
        [entityId]: uploaded.length > 0
          ? [
              ...(current[entityId] ?? []).filter((attachment) =>
                !optimistic.some((candidate) => candidate.key === attachment.key)),
              ...uploaded.map((document) => ({
                key: `legal-entity-upload-${attachmentSequence.current++}`,
                documentId: document.documentId,
                filename: document.filename,
                status: "ready" as const,
              })),
            ]
          : (current[entityId] ?? []).map((attachment) =>
              optimistic.some((candidate) => candidate.key === attachment.key)
                ? { ...attachment, status: "error" }
                : attachment),
      }));
    } catch {
      setDraftAttachments((current) => ({
        ...current,
        [entityId]: (current[entityId] ?? []).map((attachment) =>
          optimistic.some((candidate) => candidate.key === attachment.key)
            ? { ...attachment, status: "error" }
            : attachment),
      }));
    }
  };

  const save = async (structure = value) => {
    dispatch({ type: "save_started" });
    try {
      const payload = buildLegalStructureSavePayload(
        resource,
        structure,
        attachmentIdsByEntity,
      );
      if (hasReadyAttachments) {
        if (!onSaveWithAttachments || !onGetAttachmentStatus) {
          throw new Error("Legal entity attachment analysis is unavailable.");
        }
        let result: BuilderLegalEntityAttachmentMutation
          | BuilderLegalEntityAttachmentStatus = await onSaveWithAttachments(payload);
        while (result.status === "pending" || result.status === "running") {
          await new Promise((resolve) =>
            window.setTimeout(resolve, attachmentPollIntervalMs));
          result = await onGetAttachmentStatus(result.job_id);
        }
        if (result.status !== "succeeded") {
          throw new Error(
            "error" in result && result.error
              ? result.error
              : "The attached files could not be analyzed.",
          );
        }
        setDraftAttachments({});
        onRetry();
      } else {
        await onSave(payload);
      }
      dispatch({ type: "save_succeeded" });
      onDirtyChange?.(false);
    } catch (saveError) {
      dispatch({
        type: "save_failed",
        error: saveError instanceof Error && !(saveError instanceof BackendError)
          ? saveError.message
          : legalStructureSaveErrorMessage(saveError),
      });
      if (saveError instanceof BackendError && saveError.status === 409) {
        onRetry();
      }
    }
  };

  return (
    <LegalStructureEditor
      value={value}
      evidence={resource.evidence}
      consolidationSources={resource.consolidation_sources}
      consolidationCurrencies={resource.consolidation_currencies}
      analysisInterval={analysisInterval}
      editing={editorState.editing}
      dirty={editorState.dirty}
      saving={editorState.saving}
      attachmentsBusy={attachmentsBusy}
      attachmentsInvalid={attachmentsInvalid}
      canEdit={resource.can_edit}
      error={editorState.error}
      onChange={(next) => {
        dispatch({
          type: "change",
          structure: next,
          persistedStructure: resource.structure,
        });
        onDirtyChange?.(
          !legalStructuresHaveSameWritableState(next, resource.structure),
        );
      }}
      onEdit={() => dispatch({ type: "edit", structure: resource.structure })}
      onSave={() => void save()}
      onConfirmPresentationCurrency={() =>
        void save({
          ...value,
          presentation_currency_confirmed: true,
        })}
      onCancel={() => {
        dispatch({ type: "cancel" });
        setDraftAttachments({});
        onDirtyChange?.(false);
      }}
      onContinue={onContinue}
      onDownloadSource={onDownloadSource}
      persistedEntityIds={new Set(
        resource.structure.entities.map((entity) => entity.id),
      )}
      draftAttachments={relevantAttachments}
      onAttachFiles={onUploadAttachments
        ? (entityId, files) => void attachFiles(entityId, files)
        : undefined}
      onDetachAttachment={(entityId, attachmentKey) =>
        setDraftAttachments((current) => ({
          ...current,
          [entityId]: (current[entityId] ?? []).filter(
            (attachment) => attachment.key !== attachmentKey,
          ),
        }))}
      onDownloadAttachment={onDownloadAttachment
        ? (_entityId, attachment) => {
            if (attachment.documentId) {
              onDownloadAttachment(attachment.documentId, attachment.filename);
            }
          }
        : undefined}
    />
  );
}
